// src/features/merchantConfig/components/editors/UrlEditor.jsx
//
// URL editor - text input typed as `url` so mobile keyboards surface the
// right characters. Validates on blur and gives the caller the chance to
// reject the commit by passing an `onInvalid` callback.

import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { inputClass } from "./editorChrome";
import { validateUrl } from "../../lib/fieldValidators";

/**
 * @param {{
 *   value: string,
 *   onCommit: (next: string) => void,
 *   onInvalid?: (msg: string) => void,
 *   ariaLabel: string,
 *   hasError?: boolean,
 *   inputId?: string,
 * }} props
 */
export default function UrlEditor({
  value,
  onCommit,
  onInvalid,
  ariaLabel,
  hasError,
  inputId,
}) {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const commit = () => {
    if (draft === (value ?? "")) return;
    const err = validateUrl(draft);
    if (err) {
      if (onInvalid) onInvalid(err);
      return;
    }
    onCommit(draft);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        id={inputId}
        type="url"
        value={draft}
        placeholder="https://example.com/…"
        aria-label={ariaLabel}
        aria-invalid={hasError || undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(value ?? "");
            e.currentTarget.blur();
          }
        }}
        className={inputClass({ hasError })}
      />
      {draft && !hasError && (
        <a
          href={draft}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-gray-400 hover:text-blue-600"
          aria-label="Open URL in new tab"
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
      )}
    </div>
  );
}
